import {useState} from 'react'
import {useQuery, useQueries} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {Clock, CheckCircle, XCircle, Ban, User, Layers, Calendar, ChevronRight, Loader2, Shield} from 'lucide-react'
import {useParams, useLocation} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {PageLayout} from '@/components/shared/PageLayout'
import {queryError} from '@/lib/query-utils'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {DialogForm} from '@/components/shared/DialogForm'
import {Textarea} from '@/components/ui/textarea'
import {Label} from '@/components/ui/label'
import {ApprovalChain} from '@/components/ApprovalChain'
import {EscalationTimelineCard} from '@/components/shared/EscalationTimelineCard'
import {EscalationStepType} from '@/types/escalation'
import {
    RequestItemStatus,
    RequestStatus,
} from '@/types/function-group'
import {CONCEPT_ICON} from '@/lib/concepts'
import type {
    FunctionGroupRequestApprovalStep,
} from '@/types/function-group'
import type {ApprovalStep} from '@/types/access-request'
import {ApprovalStepResponseFields} from '@/types/access-request'
import {permissions} from '@/lib/permissions'
import {formatTimestamp, formatDateOnly} from '@/lib/date-utils'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {ErrorText} from '@/components/shared/ErrorText'
import {InlineRow} from '@/components/shared/InlineRow'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {Icon} from '@/components/shared/Icon'
import {TextLink} from '@/components/shared/TextLink'
import {Skeleton} from '@/components/ui/skeleton'
import {useAppConfig} from '@/hooks/use-app-config'
import type {FunctionGroupAccessProfile} from '@/types/function-group'

/**
 * Maps FunctionGroupRequestApprovalStep to ApprovalStep (structurally compatible for ApprovalChain component).
 */

function toApprovalSteps(steps: FunctionGroupRequestApprovalStep[]): ApprovalStep[] {
    return steps.map(step => ({
        id: step.id,
        stepOrder: step.stepOrder,
        [ApprovalStepResponseFields.resolverType]: step.resolverType as ApprovalStep['resolverType'],
        resolverParam: step.resolverParam,
        [ApprovalStepResponseFields.approvalStrategy]: step.approvalStrategy as ApprovalStep['approvalStrategy'],
        required: step.required,
        [ApprovalStepResponseFields.status]: step.status as ApprovalStep['status'],
        activatedAt: step.activatedAt,
        completedAt: step.completedAt,
        approvers: step.approvers,
        decisions: step.decisions,
    }))
}
 

export function FunctionGroupRequestDetail() {
    const services = useServices()
    const {id} = useParams<{ id: string }>()
    const location = useLocation()
    const {hasPermission} = useAuth()
    const i18n = useI18n()
    usePageTitle(i18n.functionGroupRequests.detailPageTitle)

    const requestStatusBadge = (status: RequestStatus) => {
        switch (status) {
            case RequestStatus.SUBMITTED:
                return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3"/>{i18n.functionGroupRequests.statusSubmitted}</Badge>
            case RequestStatus.IN_PROGRESS:
                return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3"/>{i18n.functionGroupRequests.statusInProgress}</Badge>
            case RequestStatus.COMPLETED:
                return <Badge variant="success" className="gap-1"><CheckCircle className="h-3 w-3"/>{i18n.functionGroupRequests.statusCompleted}</Badge>
            case RequestStatus.CANCELLED:
                return <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3"/>{i18n.functionGroupRequests.statusCancelled}</Badge>
        }
    }

    const itemStatusBadge = (status: RequestItemStatus) => {
        switch (status) {
            case RequestItemStatus.PENDING_APPROVAL:
                return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3"/>{i18n.functionGroupRequests.statusPendingApproval}</Badge>
            case RequestItemStatus.APPROVED:
                return <Badge variant="secondary" className="gap-1"><CheckCircle className="h-3 w-3"/>{i18n.functionGroupRequests.statusApproved}</Badge>
            case RequestItemStatus.FULFILLED:
                return <Badge variant="success" className="gap-1"><CheckCircle className="h-3 w-3"/>{i18n.functionGroupRequests.statusFulfilled}</Badge>
            case RequestItemStatus.REJECTED:
                return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3"/>{i18n.functionGroupRequests.statusRejected}</Badge>
            case RequestItemStatus.CANCELLED:
                return <Badge variant="destructive" className="gap-1"><Ban className="h-3 w-3"/>{i18n.functionGroupRequests.statusCancelled}</Badge>
        }
    }

    const {environmentTypes} = useAppConfig()
    const canViewFgAccessProfiles = hasPermission(permissions.functionGroups.view)

    const [approveDialogItem, setApproveDialogItem] = useState<string | null>(null)
    const [rejectDialogItem, setRejectDialogItem] = useState<string | null>(null)
    const [cancelDialogItem, setCancelDialogItem] = useState<string | null>(null)
    const cancelRequestDialog = useDialog()
    const [comment, setComment] = useState('')
    const [expandedApSections, setExpandedApSections] = useState<Set<string>>(new Set())

    const isMyRoute = location.pathname.startsWith(`${routes.my.root()}/`)
    const backPath = isMyRoute ? routes.my.functionGroupRequests.list() : routes.accessGovernance.functionGroupRequests.list()

    const {data: request, isLoading, error} = useQuery({
        queryKey: ['function-group-request', id],
        queryFn: () => isMyRoute
            ? services.functionGroupRequests.myRequestDetail(id!)
            : services.functionGroupRequests.detail(id!),
        enabled: !!id,
    })

    // Fetch access profiles for each request item's function group in parallel
    const itemApQueries = useQueries({
        queries: (request?.items ?? []).map(item => ({
            queryKey: ['function-group-access-profiles', item.functionGroup.id],
            queryFn: () => services.functionGroups.accessProfiles(item.functionGroup.id),
            enabled: canViewFgAccessProfiles && !!item.functionGroup.id,
        })),
    })

    const toggleApSection = (itemId: string) => {
        setExpandedApSections(prev => {
            const next = new Set(prev)
            if (next.has(itemId)) {
                next.delete(itemId)
            } else {
                next.add(itemId)
            }
            return next
        })
    }

    const approveMutation = useEntityMutation({
        mutationFn: (itemId: string) =>
            services.functionGroupRequests.approveItem(id!, itemId, {comment: comment || undefined}),
        invalidate: ['function-group-request', id],
        successMessage: i18n.functionGroupRequests.itemApproved,
        closeDialog: () => {
            setApproveDialogItem(null)
            setComment('')
        },
    })

    const rejectMutation = useEntityMutation({
        mutationFn: (itemId: string) =>
            services.functionGroupRequests.rejectItem(id!, itemId, {comment}),
        invalidate: ['function-group-request', id],
        successMessage: i18n.functionGroupRequests.itemRejected,
        closeDialog: () => {
            setRejectDialogItem(null)
            setComment('')
        },
    })

    const cancelItemMutation = useEntityMutation({
        mutationFn: (itemId: string) =>
            services.functionGroupRequests.cancelItem(id!, itemId, {reason: comment}),
        invalidate: [['function-group-request', id], ['my-fg-requests']],
        successMessage: i18n.functionGroupRequests.itemCancelled,
        closeDialog: () => {
            setCancelDialogItem(null)
            setComment('')
        },
    })

    const cancelRequestMutation = useEntityMutation({
        mutationFn: () =>
            services.functionGroupRequests.cancel(id!, {reason: comment}),
        invalidate: [['function-group-request', id], ['my-fg-requests']],
        successMessage: i18n.functionGroupRequests.requestCancelled,
        closeDialog: () => {
            cancelRequestDialog.close()
            setComment('')
        },
    })

    const hasPendingItems = request?.items.some(i => i.status === RequestItemStatus.PENDING_APPROVAL) ?? false
    const canCancel = hasPermission(permissions.functionGroupRequests.create) && hasPendingItems

    return (
        <PageLayout
            variant="detail"
            title={i18n.functionGroupRequests.title}
            icon={CONCEPT_ICON.functionGroupRequest}
            backTo={backPath}
            loading={isLoading}
            error={queryError(isLoading, request, error)}
            data-testid="function-group-request-detail-page"
            description={request ? (
                <span data-testid="request-status-badge">
                    {requestStatusBadge(request.status)}
                </span>
            ) : undefined}
            actions={canCancel ? (
                <Button data-testid="btn-cancel-request" variant="destructive" onClick={() => {
                    setComment('')
                    cancelRequestDialog.open()
                }}>
                    <IconText icon={Ban}>{i18n.functionGroupRequests.cancelRequest}</IconText>
                </Button>
            ) : undefined}
        >
            {request && <>

            <DetailGrid columns={2} className="mb-6">
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-request-details" className="font-medium text-muted-foreground">{i18n.functionGroupRequests.requestDetails}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <IconText icon={User}>
                            <MutedText>{i18n.functionGroupRequests.requester}:</MutedText>
                            <Strong>{request.requester?.displayName ?? '—'}</Strong>
                        </IconText>
                        <IconText icon={User}>
                            <MutedText>{i18n.functionGroupRequests.beneficiary}:</MutedText>
                            <Strong>{request.beneficiary?.displayName ?? '—'}</Strong>
                        </IconText>
                        <IconText icon={Calendar}>
                            <MutedText>{i18n.common.createdAt}:</MutedText>
                            <SmallText>{formatTimestamp(request.createdAt)}</SmallText>
                        </IconText>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle data-testid="card-title-justification" className="font-medium text-muted-foreground">{i18n.functionGroupRequests.justification}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <SmallText>{request.justification}</SmallText>
                    </CardContent>
                </Card>
            </DetailGrid>

            <Strong className="text-lg mb-4">{i18n.functionGroupRequests.requestItems({count: request.items.length})}</Strong>
            <Stack>
                {request.items.map((item, itemIndex) => {
                    const apQuery = itemApQueries[itemIndex]
                    const apData = apQuery?.data
                    const apLoading = apQuery?.isLoading ?? false
                    const apError = apQuery?.isError ?? false
                    const apCount = apData?.items.length ?? 0
                    const isExpanded = expandedApSections.has(item.id)
                    const showApSection = canViewFgAccessProfiles && (apLoading || apError || apCount > 0)

                    return (
                        <Card key={item.id} data-testid={`request-item-${item.id}`}>
                            <CardContent className="pt-6">
                                <Stack>
                                    <InlineRow justify="between" align="start">
                                        <Stack gap="sm">
                                            <IconText icon={Layers}>
                                                <Strong data-testid={`item-name-${item.id}`}>{item.functionGroup?.displayName ?? '—'}</Strong>
                                            </IconText>
                                            <InlineRow gap="md">
                                                <MutedText size="sm">
                                                    {item.expiresAt ? (
                                                        <span>{i18n.functionGroupRequests.untilDate({date: formatDateOnly(item.expiresAt)})}</span>
                                                    ) : (
                                                        <span>{i18n.common.permanent}</span>
                                                    )}
                                                </MutedText>
                                                {item.matchedPolicyName && (
                                                    <MutedText size="xs">{i18n.approvalChain.matchedPolicy}: {item.matchedPolicyName}</MutedText>
                                                )}
                                            </InlineRow>
                                            <InlineRow gap="sm" data-testid={`item-status-${item.id}`}>
                                                {itemStatusBadge(item.status)}
                                            </InlineRow>
                                        </Stack>

                                        <InlineRow>
                                            {item.status === RequestItemStatus.PENDING_APPROVAL && canCancel && request.items.length > 1 && (
                                                <Button data-testid={`btn-cancel-item-${item.id}`} size="sm"
                                                        variant="outline" onClick={() => {
                                                    setComment('')
                                                    setCancelDialogItem(item.id)
                                                }}>
                                                    <Icon icon={Ban} size="xs" className="mr-1"/>
                                                    {i18n.common.cancel}
                                                </Button>
                                            )}
                                        </InlineRow>
                                    </InlineRow>

                                    {item.status !== RequestItemStatus.CANCELLED && item.approvalChain && item.approvalChain.length > 0 && (
                                        <Stack leftBorder className="mt-4">
                                            <MutedText size="xs" block uppercase weight="medium" className="mb-3">
                                                {i18n.approvalChain.title}
                                            </MutedText>
                                            <ApprovalChain
                                                steps={toApprovalSteps(item.approvalChain)}
                                                itemId={item.id}
                                                onApprove={(itemId) => {
                                                    setComment('')
                                                    setApproveDialogItem(itemId)
                                                }}
                                                onReject={(itemId) => {
                                                    setComment('')
                                                    setRejectDialogItem(itemId)
                                                }}
                                                requiredPermission={permissions.functionGroupRequests.approve}
                                            />
                                        </Stack>
                                    )}
                                    {item.approvalChain
                                        ?.filter(step => step.status === 'ACTIVE')
                                        .map(step => (
                                            <EscalationTimelineCard
                                                key={`escalation-${step.id}`}
                                                stepId={step.id}
                                                stepType={EscalationStepType.FUNCTION_GROUP_REQUEST}
                                                escalationLevel={step.escalationLevel}
                                                data-testid={`escalation-timeline-${step.id}`}
                                            />
                                        ))}

                                    {item.cancelReason && (
                                        <ErrorText className="mt-2 block">
                                            {i18n.functionGroupRequests.cancelled} {item.cancelReason}
                                        </ErrorText>
                                    )}

                                    {showApSection && (
                                        <Stack topBorder>
                                            {apLoading ? (
                                                <InlineRow gap="sm">
                                                    <Icon icon={Loader2} size="xs" spin/>
                                                    <Skeleton className="h-4 w-40"/>
                                                </InlineRow>
                                            ) : (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="w-full justify-start"
                                                    onClick={() => toggleApSection(item.id)}
                                                    data-testid={`btn-toggle-ap-section-${item.id}`}
                                                >
                                                    <Icon icon={ChevronRight} rotated={isExpanded}/>
                                                    <IconText icon={Shield} size="sm">
                                                        {i18n.requestDetailLinkedAccessProfiles.sectionTitle({count: apCount})}
                                                    </IconText>
                                                </Button>
                                            )}

                                            {!apLoading && isExpanded && (
                                                <Stack className="pl-5">
                                                    {apError ? (
                                                        <InlineRow gap="sm">
                                                            <ErrorText>{i18n.requestDetailLinkedAccessProfiles.errorText}</ErrorText>
                                                            <Button
                                                                type="button"
                                                                variant="link"
                                                                size="sm"
                                                                data-testid={`btn-retry-ap-${item.id}`}
                                                                onClick={() => apQuery?.refetch()}
                                                            >
                                                                {i18n.requestDetailLinkedAccessProfiles.retry}
                                                            </Button>
                                                        </InlineRow>
                                                    ) : (
                                                        <Stack gap="sm">
                                                            {(apData?.items ?? []).map((ap: FunctionGroupAccessProfile) => {
                                                                const envLabel = environmentTypes.find(e => e.code === ap.environment)?.label ?? ap.environment
                                                                return (
                                                                    <InlineRow key={ap.accessProfile.id} gap="sm" className="items-center">
                                                                        <TextLink
                                                                            to={routes.accessGovernance.profiles.detail(ap.accessProfile.id)}
                                                                            testId={`ap-link-${ap.accessProfile.id}`}
                                                                        >
                                                                            {ap.accessProfile.displayName}
                                                                        </TextLink>
                                                                        <MutedText size="xs">{envLabel}</MutedText>
                                                                        <RiskLevelBadge riskLevel={ap.riskLevel} className="text-xs"/>
                                                                    </InlineRow>
                                                                )
                                                            })}
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            )}
                                        </Stack>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    )
                })}
            </Stack>

            {/* Approve Dialog */}
            <Dialog open={!!approveDialogItem} onOpenChange={() => setApproveDialogItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.functionGroupRequests.approveItem}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault();
                        if (approveDialogItem) approveMutation.mutate(approveDialogItem)
                    }}>
                        <div>
                            <Label htmlFor="approve-comment">{i18n.functionGroupRequests.commentOptional}</Label>
                            <Textarea id="approve-comment" value={comment}
                                      onChange={e => setComment(e.target.value)}
                                      placeholder={i18n.functionGroupRequests.commentPlaceholder}/>
                        </div>
                        <SaveCancelFooter
                            testIdPrefix="approve-dialog"
                            onCancel={() => setApproveDialogItem(null)}
                            saving={approveMutation.isPending}
                            saveLabel={i18n.common.approve}
                            savingLabel={i18n.functionGroupRequests.approving}
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={!!rejectDialogItem} onOpenChange={() => setRejectDialogItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.functionGroupRequests.rejectItem}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault();
                        if (rejectDialogItem) rejectMutation.mutate(rejectDialogItem)
                    }}>
                        <div>
                            <Label htmlFor="reject-comment">{i18n.functionGroupRequests.reasonRequired}</Label>
                            <Textarea id="reject-comment" value={comment}
                                      onChange={e => setComment(e.target.value)}
                                      placeholder={i18n.functionGroupRequests.rejectReasonPlaceholder}/>
                        </div>
                        <SaveCancelFooter
                            testIdPrefix="reject-dialog"
                            onCancel={() => setRejectDialogItem(null)}
                            saving={rejectMutation.isPending}
                            disabled={!comment.trim()}
                            saveLabel={i18n.common.reject}
                            savingLabel={i18n.functionGroupRequests.rejecting}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Cancel Item Dialog */}
            <Dialog open={!!cancelDialogItem} onOpenChange={() => setCancelDialogItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.functionGroupRequests.cancelItem}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault();
                        if (cancelDialogItem) cancelItemMutation.mutate(cancelDialogItem)
                    }}>
                        <div>
                            <Label htmlFor="cancel-reason">{i18n.functionGroupRequests.cancelReason}</Label>
                            <Textarea id="cancel-reason" value={comment}
                                      onChange={e => setComment(e.target.value)}
                                      placeholder={i18n.functionGroupRequests.cancelReasonPlaceholder}/>
                        </div>
                        <SaveCancelFooter
                            testIdPrefix="cancel-item-dialog"
                            onCancel={() => setCancelDialogItem(null)}
                            cancelLabel={i18n.common.close}
                            saving={cancelItemMutation.isPending}
                            disabled={!comment.trim()}
                            saveLabel={i18n.functionGroupRequests.cancelItem}
                            savingLabel={i18n.functionGroupRequests.cancelling}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>

            {/* Cancel Request Dialog */}
            <Dialog open={cancelRequestDialog.isOpen} onOpenChange={() => cancelRequestDialog.close()}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.functionGroupRequests.cancelEntireRequest}</DialogTitle>
                    </DialogHeader>
                    <DialogForm onSubmit={(e) => {
                        e.preventDefault();
                        cancelRequestMutation.mutate()
                    }}>
                        <div>
                            <Label htmlFor="cancel-request-reason">{i18n.functionGroupRequests.cancelReason}</Label>
                            <Textarea id="cancel-request-reason" value={comment}
                                      onChange={e => setComment(e.target.value)}
                                      placeholder={i18n.functionGroupRequests.cancelReasonPlaceholder}/>
                        </div>
                        <SaveCancelFooter
                            testIdPrefix="cancel-request-dialog"
                            onCancel={() => cancelRequestDialog.close()}
                            cancelLabel={i18n.common.close}
                            saving={cancelRequestMutation.isPending}
                            disabled={!comment.trim()}
                            saveLabel={i18n.functionGroupRequests.cancelRequest}
                            savingLabel={i18n.functionGroupRequests.cancelling}
                            destructive
                        />
                    </DialogForm>
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
